import { useEffect, useRef, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';

// How long to wait after an `offline` event before probing
const OFFLINE_DEBOUNCE_MS = 800;

/**
 * Probes real network reachability by fetching a static asset from the same
 * origin. Unlike `navigator.onLine`, this confirms the browser can actually
 * complete an HTTP request.
 */
const probeConnection = async (): Promise<boolean> => {
  try {
    const res = await fetch('/favicon.ico', {
      method: 'HEAD',
      cache: 'no-store',
    });
    return res.ok;
  } catch {
    return false;
  }
};

const useNetworkConnectionStatusImpl = (): boolean => {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  });

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setIsOnline(true);
    };

    const handleOffline = () => {
      debounceTimerRef.current = setTimeout(async () => {
        debounceTimerRef.current = null;
        const canReach = await probeConnection();
        setIsOnline(canReach);
      }, OFFLINE_DEBOUNCE_MS);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return isOnline;
};

export const useNetworkConnectionStatus = singletonHook(
  true,
  useNetworkConnectionStatusImpl,
);
